import styled from 'styled-components';

export type ShippingMode = 'free' | 'fixed' | 'threshold';

type Props = {
  mode: ShippingMode;
  baseDeliveryPrice: number;
  freeShippingThreshold: number;
  jejuDeliveryPrice: number;
  remoteDeliveryPrice: number;
  disabled?: boolean;
  onModeChange: (mode: ShippingMode) => void;
  onBaseDeliveryPriceChange: (value: number) => void;
  onFreeShippingThresholdChange: (value: number) => void;
  onJejuDeliveryPriceChange: (value: number) => void;
  onRemoteDeliveryPriceChange: (value: number) => void;
};

const money = (value: number) => Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;

export default function ShippingPolicyFields({ mode, baseDeliveryPrice, freeShippingThreshold, jejuDeliveryPrice, remoteDeliveryPrice, disabled,
  onModeChange, onBaseDeliveryPriceChange, onFreeShippingThresholdChange, onJejuDeliveryPriceChange, onRemoteDeliveryPriceChange }: Props) {
  return <Fieldset disabled={disabled}>
    <legend>배송비 설정</legend>
    <Option $selected={mode === 'free'}>
      <input type="radio" name="shipping-mode" value="free" checked={mode === 'free'}
        onChange={() => onModeChange('free')} />
      <span><strong>무료배송</strong><small>이 방송의 모든 주문에 배송비를 부과하지 않습니다.</small></span>
    </Option>
    <Option $selected={mode === 'fixed'}>
      <input type="radio" name="shipping-mode" value="fixed" checked={mode === 'fixed'} onChange={() => onModeChange('fixed')} />
      <span><strong>유료배송</strong><small>구매금액과 관계없이 설정한 배송비를 적용합니다.</small></span>
    </Option>
    <Option $selected={mode === 'threshold'}>
      <input type="radio" name="shipping-mode" value="threshold" checked={mode === 'threshold'}
        onChange={() => onModeChange('threshold')} />
      <span><strong>구매금액 기준 무료배송</strong><small>상품 합계가 기준 이상이면 무료배송을 적용합니다.</small></span>
    </Option>
    {mode !== 'free' && <MoneyGrid>
      <label>기본 배송비 <span className="required" aria-hidden="true">*</span>
        <input aria-label="기본 배송비" aria-required="true" type="number" inputMode="numeric" min={1} step={100}
          value={baseDeliveryPrice || ''} onChange={event => onBaseDeliveryPriceChange(money(Number(event.target.value)))} />
        <span>원</span>
      </label>
      {mode === 'threshold' && <label>무료배송 기준 <span className="required" aria-hidden="true">*</span>
        <input aria-label="무료배송 기준" aria-required="true" type="number" inputMode="numeric" min={1} step={1000}
          value={freeShippingThreshold || ''} onChange={event => onFreeShippingThresholdChange(money(Number(event.target.value)))} />
        <span>원 이상</span>
      </label>}
      <label>제주 배송비 <span className="required" aria-hidden="true">*</span>
        <input aria-label="제주 배송비" aria-required="true" type="number" inputMode="numeric" min={1} step={100}
          value={jejuDeliveryPrice || ''} onChange={event => onJejuDeliveryPriceChange(money(Number(event.target.value)))} />
        <span>원</span>
      </label>
      <label>도서산간 배송비 <span className="required" aria-hidden="true">*</span>
        <input aria-label="도서산간 배송비" aria-required="true" type="number" inputMode="numeric" min={1} step={100}
          value={remoteDeliveryPrice || ''} onChange={event => onRemoteDeliveryPriceChange(money(Number(event.target.value)))} />
        <span>원</span>
      </label>
    </MoneyGrid>}
  </Fieldset>;
}

const Fieldset = styled.fieldset`
  border: 0; padding: 0; margin: 0; min-width: 0;
  legend { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
  &:disabled { opacity: .65; }
`;
const Option = styled.label<{ $selected: boolean }>`
  display: flex; align-items: flex-start; gap: 10px; min-height: 64px; box-sizing: border-box;
  padding: 13px; margin-bottom: 8px; border: 1px solid ${props => props.$selected ? 'var(--admin-accent)' : 'var(--admin-theme-border, #e1dce8)'};
  border-radius: 10px; background: ${props => props.$selected ? 'var(--admin-theme-accent-soft, #f2edff)' : 'var(--admin-theme-surface, #fff)'}; cursor: pointer;
  input { width: 18px; height: 18px; margin: 2px 0 0; accent-color: var(--admin-theme-link, #6338dc); }
  span { display: flex; flex-direction: column; gap: 4px; }
  strong { color: var(--admin-ink); font-size: 15px; }
  small { color: var(--admin-theme-muted, #625d69); font-size: 13px; line-height: 1.45; }
  &:focus-within { outline: 3px solid var(--admin-theme-focus, #b7a4ef); outline-offset: 2px; }
`;
const MoneyGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px 10px; margin-top: 16px;
  label { position: relative; color: var(--admin-theme-text, #444); font-size: 14px; }
  input { width: 100%; min-height: 44px; box-sizing: border-box; margin-top: 8px; padding: 12px 58px 12px 12px; border: 1px solid var(--admin-theme-border, #d4cedd); border-radius: 8px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-text, #29242f); font-size: 16px; font-variant-numeric: tabular-nums; }
  input:focus { outline: 3px solid var(--admin-theme-focus, #b7a4ef); outline-offset: 1px; border-color: var(--admin-theme-link, var(--admin-accent)); }
  label span { position: absolute; right: 12px; bottom: 13px; color: var(--admin-theme-muted, #666); font-size: 13px; }
  label .required { position: static; color: var(--admin-theme-danger, #d84059); font-weight: 800; }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
